import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import RobustSelect from '../../components/RobustSelect';
import { SelectItem } from '../../components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, Users, FileText, Award, BarChart3, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import api from '../../lib/api';
import { toast } from 'sonner';
import LaTeXRenderer from '../../components/LaTeXRenderer';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

function AnalyticsNew() {
  const [activeTab, setActiveTab] = useState('test-wise');
  
  // Filters
  const [tests, setTests] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedTest, setSelectedTest] = useState('');
  const [selectedBatch, setSelectedBatch] = useState('');
  
  // Data
  const [testAnalytics, setTestAnalytics] = useState(null);
  const [batchAnalytics, setBatchAnalytics] = useState(null);
  const [batchTestAnalytics, setBatchTestAnalytics] = useState(null);
  const [questionAnalytics, setQuestionAnalytics] = useState(null);
  
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchTests();
    fetchBatches();
  }, []);

  useEffect(() => {
    if (activeTab === 'test-wise' && selectedTest) {
      fetchTestAnalytics(selectedTest);
      fetchQuestionAnalytics(selectedTest);
    } else if (activeTab === 'batch-wise' && selectedBatch && selectedTest && selectedTest !== 'all') {
      fetchBatchTestAnalytics(selectedBatch, selectedTest);
    } else if (activeTab === 'batch-wise' && selectedBatch && (!selectedTest || selectedTest === 'all')) {
      fetchBatchAnalytics(selectedBatch);
    } else if (activeTab === 'question-wise' && selectedTest) {
      fetchQuestionAnalytics(selectedTest);
    }
  }, [activeTab, selectedTest, selectedBatch]);

  const fetchTests = async () => {
    try {
      const response = await api.get('/tests');
      setTests(response.data);
    } catch (error) {
      console.error('Error fetching tests:', error);
      toast.error('Failed to fetch tests');
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await api.get('/batches');
      setBatches(response.data);
    } catch (error) {
      console.error('Error fetching batches:', error);
      toast.error('Failed to fetch batches');
    }
  };

  const fetchTestAnalytics = async (testId) => {
    setLoading(true);
    try {
      const response = await api.get(`/analytics/test/${testId}`);
      setTestAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching test analytics:', error);
      toast.error('Failed to fetch test analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchBatchAnalytics = async (batchId) => {
    setLoading(true);
    try {
      const response = await api.get(`/analytics/batch/${batchId}`);
      setBatchAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching batch analytics:', error);
      toast.error('Failed to fetch batch analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchBatchTestAnalytics = async (batchId, testId) => {
    setLoading(true);
    try {
      const response = await api.get(`/analytics/batch/${batchId}/test/${testId}`);
      setBatchTestAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching batch-test analytics:', error);
      toast.error('Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestionAnalytics = async (testId) => {
    try {
      const response = await api.get(`/analytics/test/${testId}/questions`);
      setQuestionAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching question analytics:', error);
    }
  };


  const downloadFile = (base64Data, filename) => {
    const link = document.createElement('a');
    link.href = `data:application/octet-stream;base64,${base64Data}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportTestAnalyticsPDF = async () => {
    if (!selectedTest) {
      toast.error('Please select a test first');
      return;
    }

    setExporting(true);
    try {
      const response = await api.get(`/export/analytics/test/${selectedTest}/pdf`);
      downloadFile(response.data.data, response.data.filename);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  const formatScoreDistribution = (scoreDistribution) => {
    if (!scoreDistribution) return [];
    return Object.entries(scoreDistribution).map(([range, count]) => ({
      range,
      count,
    }));
  };

  const renderStatCards = (stats) => {
    if (!stats) return null;
    
    const cards = [
      {
        title: 'Total Attempts',
        value: stats.total_attempts || 0,
        icon: Users,
        color: 'bg-blue-100 text-blue-600',
      },
      {
        title: 'Average Score',
        value: `${stats.average_percentage?.toFixed(1) || 0}%`,
        icon: TrendingUp,
        color: 'bg-green-100 text-green-600',
      },
      {
        title: 'Pass Rate',
        value: `${stats.pass_rate?.toFixed(1) || 0}%`,
        icon: Award,
        color: 'bg-purple-100 text-purple-600',
      },
      {
        title: stats.tests_taken !== undefined ? 'Tests Taken' : 'Highest Score',
        value: stats.tests_taken !== undefined ? stats.tests_taken : (stats.highest_score?.toFixed(1) || 0),
        icon: FileText,
        color: 'bg-orange-100 text-orange-600',
      },
    ];

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {cards.map((card, idx) => {
          const Icon = card.icon;
          return (
            <Card key={idx} className="p-6">
              <div className="flex items-center">
                <div className={`p-3 rounded-full ${card.color} mr-4`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">{card.title}</p>
                  <p className="text-2xl font-bold text-gray-900">{card.value}</p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    );
  };

  const renderScoreDistribution = (scoreDistribution) => {
    const data = formatScoreDistribution(scoreDistribution);
    if (data.length === 0) return null;

    return (
      <Card className="p-6 mb-8">
        <h3 className="text-lg font-semibold mb-4">Score Distribution</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="range" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#3b82f6" name="Students" />
            </BarChart>
          </ResponsiveContainer>
          <div>
            <h4 className="text-md font-medium mb-3 text-center text-gray-700">Score Range Distribution (%)</h4>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  label={({ range, percent }) => `${range}%: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={90}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value, name, props) => [`${value} students`, `Score Range: ${props.payload.range}%`]}
                />
                <Legend 
                  formatter={(value, entry) => `${entry.payload.range}%: ${entry.payload.count} students`}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Card>
    );
  };

  const renderQuestionTable = () => {
    if (!questionAnalytics || !questionAnalytics.questions || questionAnalytics.questions.length === 0) {
      return null;
    }

    return (
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Question-wise Performance</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Question</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">Difficulty</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">Correct</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">Incorrect</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">Skipped</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">Accuracy</th>
              </tr>
            </thead>
            <tbody>
              {questionAnalytics.questions.map((q, idx) => (
                <tr key={q.question_id} className="border-b hover:bg-gray-50">
                  <td className="py-3 px-4 text-sm text-gray-900">
                    <span className="font-medium">Q{idx + 1}:</span>{' '}
                    <LaTeXRenderer text={q.question_text.substring(0, 60) + (q.question_text.length > 60 ? '...' : '')} className="inline" />
                  </td>
                  <td className="text-center py-3 px-4">
                    <span className={`inline-block px-2 py-1 rounded-full text-xs ${
                      q.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                      q.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {q.difficulty}
                    </span>
                  </td>
                  <td className="text-center py-3 px-4 text-sm text-green-600 font-medium">
                    {q.correct_count}
                  </td>
                  <td className="text-center py-3 px-4 text-sm text-red-600 font-medium">
                    {q.incorrect_count}
                  </td>
                  <td className="text-center py-3 px-4 text-sm text-gray-500 font-medium">
                    {q.skipped_count}
                  </td>
                  <td className="text-center py-3 px-4 text-sm font-medium">
                    <span className={`inline-block px-2 py-1 rounded-full text-xs ${
                      q.accuracy >= 70 ? 'bg-green-100 text-green-700' :
                      q.accuracy >= 40 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {q.accuracy.toFixed(1)}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    );
  };

  return (
    <Layout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Analytics Dashboard</h1>
        <p className="text-gray-600">Comprehensive performance insights and statistics</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-grid">
          <TabsTrigger value="test-wise" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            <span>Test-wise</span>
          </TabsTrigger>
          <TabsTrigger value="batch-wise" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            <span>Batch-wise</span>
          </TabsTrigger>
          <TabsTrigger value="question-wise" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            <span>Question-wise</span>
          </TabsTrigger>
        </TabsList>

        {/* Test-wise Analysis */}
        <TabsContent value="test-wise" className="space-y-6">
          <Card className="p-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 max-w-md">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Test
                </label>
                <RobustSelect
                  value={selectedTest}
                  onValueChange={setSelectedTest}
                  placeholder="Choose a test"
                  testId="test-selector"
                >
                  {tests.map((test) => (
                    <SelectItem key={test.id} value={test.id}>
                      {test.title}
                    </SelectItem>
                  ))}
                </RobustSelect>
              </div>
              {selectedTest && (
                <div className="pt-6">
                  <Button
                    variant="outline"
                    onClick={handleExportTestAnalyticsPDF}
                    disabled={exporting}
                    className="flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    {exporting ? 'Exporting...' : 'Export PDF'}
                  </Button>
                </div>
              )}
            </div>
          </Card>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : testAnalytics && selectedTest ? (
            <>
              {renderStatCards(testAnalytics)}
              {renderScoreDistribution(testAnalytics.score_distribution)}
              {renderQuestionTable()}
            </>
          ) : (
            <Card className="p-12 text-center">
              <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <p className="text-gray-500">Select a test to view analytics</p>
            </Card>
          )}
        </TabsContent>

        {/* Batch-wise Analysis */}
        <TabsContent value="batch-wise" className="space-y-6">
          <Card className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Batch
                </label>
                <RobustSelect
                  value={selectedBatch}
                  onValueChange={setSelectedBatch}
                  placeholder="Choose a batch"
                  testId="batch-selector"
                >
                  {batches.map((batch) => (
                    <SelectItem key={batch.id} value={batch.id}>
                      {batch.name} {batch.year && `(${batch.year})`}
                    </SelectItem>
                  ))}
                </RobustSelect>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filter by Test (Optional)
                </label>
                <RobustSelect
                  value={selectedTest}
                  onValueChange={setSelectedTest}
                  placeholder="All tests"
                  testId="test-filter"
                >
                  <SelectItem value="all">All Tests</SelectItem>
                  {tests.map((test) => (
                    <SelectItem key={test.id} value={test.id}>
                      {test.title}
                    </SelectItem>
                  ))}
                </RobustSelect>
              </div>
            </div>
          </Card>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : (batchTestAnalytics || batchAnalytics) && selectedBatch ? (
            <>
              {renderStatCards(batchTestAnalytics || batchAnalytics)}
              {renderScoreDistribution((batchTestAnalytics || batchAnalytics)?.score_distribution)}
            </>
          ) : (
            <Card className="p-12 text-center">
              <Users className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <p className="text-gray-500">Select a batch to view analytics</p>
            </Card>
          )}
        </TabsContent>

        {/* Question-wise Analysis */}
        <TabsContent value="question-wise" className="space-y-6">
          <Card className="p-6">
            <div className="max-w-md">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Test
              </label>
              <RobustSelect
                value={selectedTest}
                onValueChange={setSelectedTest}
                placeholder="Choose a test"
                testId="question-test-selector"
              >
                {tests.map((test) => (
                  <SelectItem key={test.id} value={test.id}>
                    {test.title}
                  </SelectItem>
                ))}
              </RobustSelect>
            </div>
          </Card>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : questionAnalytics && selectedTest ? (
            <>
              {renderQuestionTable()}
            </>
          ) : (
            <Card className="p-12 text-center">
              <BarChart3 className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <p className="text-gray-500">Select a test to view question-wise analytics</p>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </Layout>
  );
}

export default AnalyticsNew;
